/**
 * AzureDiagramPage - "Schema Azure" : le schema d architecture de l abonnement
 * audite, genere a partir du meme scan que les controles AZ-*.
 *
 * Flux : bouton "Faire le schema d Azure" -> POST /clients/azure-scans (le worker
 * RunAzureScan enchaine les 67 controles puis la collecte de topologie) -> polling
 * du scan -> GET /clients/azure-topologies/{scanId} -> rendu.
 *
 * Le rendu est celui de l outil HYBEOS AzureDescriptor : son gabarit autonome est
 * rempli cote client (src/lib/azureDiagram.ts) et affiche dans une iframe. On
 * herite ainsi de ses filtres, de son panneau de details et de son export PNG
 * sans reecrire le moteur de disposition.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Card, Title3, Body1, Caption1, Button, Spinner, Dropdown, Option, ProgressBar, Badge,
  Menu, MenuTrigger, MenuButton, MenuPopover, MenuList, MenuItem,
  MessageBar, MessageBarBody, MessageBarTitle, tokens,
} from '@fluentui/react-components';
import {
  Play24Regular, ArrowSync20Regular, ArrowDownload20Regular, Open20Regular,
} from '@fluentui/react-icons';
import { PageHeader } from '../components/PageHeader';
import { azureConnectionsApi, type AzureConnection } from '../api/azureConnectionsApi';
import { azureScansApi } from '../api/azureScansApi';
import { azureTopologyApi, type AzureTopologyDoc, type AzureTopologyRow } from '../api/azureTopologyApi';
import { buildDiagramHtml, diagramFileName, loadDiagramAssets } from '../lib/azureDiagram';

/** Intervalle et plafond du polling pendant qu un scan tourne (~15 min). */
const POLL_MS = 4000;
const POLL_MAX = 225;

export function AzureDiagramPage() {
  const { t, i18n } = useTranslation();

  const [connections, setConnections]       = useState<AzureConnection[]>([]);
  const [selectedConnId, setSelectedConnId] = useState<string | null>(null);
  const [doc, setDoc]                       = useState<AzureTopologyDoc | null>(null);
  const [history, setHistory]               = useState<AzureTopologyRow[]>([]);
  const [selectedScanId, setSelectedScanId] = useState<string | null>(null);
  const [html, setHtml]                     = useState<string | null>(null);
  const [loading, setLoading]               = useState(true);
  const [generating, setGenerating]         = useState(false);
  const [progress, setProgress]             = useState<string | null>(null);
  const [exporting, setExporting]           = useState<'drawio' | 'vsdx' | null>(null);
  const [error, setError]                   = useState<string | null>(null);

  const cancelled = useRef(false);
  useEffect(() => () => { cancelled.current = true; }, []);

  // ---------------------- Chargement ----------------------
  useEffect(() => { void loadConnections(); }, []);

  useEffect(() => {
    if (!selectedConnId) return;
    void loadHistory(selectedConnId);
  }, [selectedConnId]);

  async function loadConnections() {
    setLoading(true);
    setError(null);
    try {
      const { connections: cs } = await azureConnectionsApi.list();
      setConnections(cs ?? []);
      if (cs?.length) setSelectedConnId((prev) => prev ?? cs[0].id);
      else setLoading(false);
    } catch (e) {
      const err = e as Error & { status?: number };
      if (err.status !== 404) setError(err.message);
      setLoading(false);
    }
  }

  /** Charge l historique des releves et affiche le plus recent. */
  async function loadHistory(connectionId: string, preferScanId?: string) {
    setLoading(true);
    setError(null);
    setHtml(null);
    try {
      const { topologies } = await azureTopologyApi.list(connectionId);
      const rows = topologies ?? [];
      setHistory(rows);

      const wanted = (preferScanId && rows.some((r) => r.id === preferScanId))
        ? preferScanId
        : rows[0]?.id ?? null;

      setSelectedScanId(wanted);
      if (wanted) {
        await loadTopology(wanted);
      } else {
        setDoc(null);
      }
    } catch (e) {
      const err = e as Error & { status?: number };
      if (err.status !== 404) setError(err.message);
      setHistory([]);
      setDoc(null);
    } finally {
      setLoading(false);
    }
  }

  /** Charge et affiche un releve precis. */
  async function loadTopology(scanId: string) {
    setHtml(null);
    try {
      const d = await azureTopologyApi.get(scanId);
      setDoc(d);
      await render(d);
    } catch (e) {
      const err = e as Error & { status?: number };
      setDoc(null);
      setError(err.status === 404
        ? t('azureDiagram.missing', 'Ce relevé n’est plus disponible.')
        : err.message);
    }
  }

  const render = useCallback(async (d: AzureTopologyDoc) => {
    const assets = await loadDiagramAssets();
    setHtml(buildDiagramHtml(assets, d.dataset, {
      language:  i18n.language?.toLowerCase().startsWith('en') ? 'EN' : 'FR',
      generated: new Date(d.generatedAt),
    }));
  }, [i18n.language]);

  // ---------------------- Generation ----------------------
  async function handleGenerate() {
    if (!selectedConnId) return;
    setGenerating(true);
    setError(null);
    setProgress(t('azureDiagram.progressQueued', 'Scan en file d attente…'));
    try {
      const { scanId } = await azureScansApi.trigger(selectedConnId);
      let attempts = 0;

      const poll = async (): Promise<void> => {
        if (cancelled.current) return;
        attempts++;

        // Le polling tourne hors du try/catch de handleGenerate (appel via
        // `void poll()`) : sans ce catch, une API en erreur devient une promesse
        // rejetee silencieuse et la page reste figee sur le premier message.
        let scan;
        try {
          scan = await azureScansApi.get(scanId);
        } catch (e) {
          const err = e as Error & { status?: number };
          setGenerating(false);
          setProgress(null);
          setError(err.status === 404
            ? t('azureDiagram.pollMissing', 'Suivi du scan indisponible (scan {{id}} introuvable). Le scan tourne peut-être encore côté serveur : rouvrez la page dans quelques minutes.', { id: scanId })
            : t('azureDiagram.pollFailed', 'Suivi du scan interrompu : {{msg}}', { msg: err.message }));
          return;
        }

        if (scan.status === 'queued' && attempts > 15) {
          // ~1 min en file : le worker ne consomme pas la queue azure-scan-requests.
          setProgress(t(
            'azureDiagram.progressStuck',
            'Toujours en file d attente — le worker de scan ne semble pas consommer la demande.',
          ));
        }

        if (scan.status === 'running' && scan.progress) {
          setProgress(t(
            'azureDiagram.progressChecks',
            'Contrôles {{current}}/{{total}} — ensuite le relevé des ressources',
            { current: scan.progress.current, total: scan.progress.total },
          ));
        }

        if (scan.status === 'error') {
          setGenerating(false);
          setProgress(null);
          setError(scan.errorMessage ?? t('azureDiagram.scanFailed', 'Le scan a échoué.'));
          return;
        }

        if (scan.status === 'done') {
          // Le worker ecrit la topologie apres avoir marque le scan "done" :
          // le resume `topology` apparait au rafraichissement suivant.
          const topo = (scan as { topology?: { status: string; error?: string } }).topology;
          if (!topo) {
            if (attempts < POLL_MAX) { setTimeout(() => void poll(), POLL_MS); return; }
            setGenerating(false); setProgress(null);
            setError(t('azureDiagram.noTopology', 'Le scan est terminé mais aucun schéma n’a été produit.'));
            return;
          }
          if (topo.status === 'error') {
            setGenerating(false); setProgress(null);
            setError(t('azureDiagram.topologyFailed', 'Relevé des ressources impossible : {{msg}}', { msg: topo.error ?? '' }));
            return;
          }
          setProgress(t('azureDiagram.progressRender', 'Construction du schéma…'));
          if (selectedConnId) {
            await loadHistory(selectedConnId, scanId);
          } else {
            await loadTopology(scanId);
          }
          setGenerating(false);
          setProgress(null);
          return;
        }

        if (attempts >= POLL_MAX) {
          setGenerating(false); setProgress(null);
          setError(t('azureDiagram.timeout', 'Le scan dépasse le délai d’attente. Rouvrez la page plus tard : le schéma s’affichera dès qu’il sera prêt.'));
          return;
        }
        setTimeout(() => void poll(), POLL_MS);
      };

      void poll();
    } catch (e) {
      setGenerating(false);
      setProgress(null);
      setError((e as Error).message);
    }
  }

  // ---------------------- Telechargement ----------------------
  function handleDownload() {
    if (!html || !doc) return;
    const blob = new Blob([html], { type: 'text/html;charset=utf-8' });
    const url  = URL.createObjectURL(blob);
    const a    = document.createElement('a');
    a.href = url;
    a.download = diagramFileName(doc.dataset, doc.generatedAt);
    a.click();
    setTimeout(() => URL.revokeObjectURL(url), 2000);
  }

  /** Telecharge le releve converti en .drawio ou .vsdx (conversion cote serveur). */
  async function handleExport(format: 'drawio' | 'vsdx') {
    if (!doc) return;
    setExporting(format);
    setError(null);
    try {
      const lang = i18n.language?.toLowerCase().startsWith('en') ? 'EN' : 'FR';
      const res  = await azureTopologyApi.export(doc.id, format, lang);

      // base64 -> octets : atob rend une chaine binaire, il faut la recopier
      // octet par octet, sinon les caracteres > 0x7F sont mal encodes.
      const bin   = atob(res.base64);
      const bytes = new Uint8Array(bin.length);
      for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);

      const url = URL.createObjectURL(new Blob([bytes], { type: res.contentType }));
      const a   = document.createElement('a');
      a.href = url;
      a.download = res.fileName;
      a.click();
      setTimeout(() => URL.revokeObjectURL(url), 2000);
    } catch (e) {
      setError(t('azureDiagram.exportFailed', 'Conversion impossible : {{msg}}', { msg: (e as Error).message }));
    } finally {
      setExporting(null);
    }
  }

  function handleOpenTab() {
    if (!html) return;
    const blob = new Blob([html], { type: 'text/html;charset=utf-8' });
    window.open(URL.createObjectURL(blob), '_blank', 'noopener');
  }

  /** Libelle d un releve dans le selecteur : date + compteurs quand on les a. */
  function historyLabel(row: AzureTopologyRow): string {
    const when = new Date(row.generatedAt).toLocaleString();
    const n    = row.summary?.resources;
    return n != null ? `${when} — ${n} ${t('azureDiagram.resources', 'ressources')}` : when;
  }

  // ---------------------- Rendus ----------------------
  const selectedConn = useMemo(
    () => connections.find((c) => c.id === selectedConnId) ?? null,
    [connections, selectedConnId],
  );

  const meta = doc?.dataset.meta;

  return (
    <>
      <PageHeader
        title={t('azureDiagram.title', 'Schéma Azure')}
        description={t('azureDiagram.subtitle', 'Architecture de l’abonnement audité, relevée pendant le scan Azure.')}
        actions={
          <>
            {doc && (
              <>
                <Button appearance="subtle" icon={<Open20Regular />} onClick={handleOpenTab} disabled={!html}>
                  {t('azureDiagram.openTab', 'Ouvrir en plein écran')}
                </Button>
                <Menu>
                  <MenuTrigger disableButtonEnhancement>
                    <MenuButton appearance="subtle" icon={<ArrowDownload20Regular />} disabled={!!exporting}>
                      {exporting
                        ? t('azureDiagram.exporting', 'Conversion…')
                        : t('azureDiagram.exportMenu', 'Télécharger')}
                    </MenuButton>
                  </MenuTrigger>
                  <MenuPopover>
                    <MenuList>
                      <MenuItem disabled={!html} onClick={handleDownload}>
                        {t('azureDiagram.downloadHtml', 'Page interactive (.html)')}
                      </MenuItem>
                      <MenuItem onClick={() => void handleExport('drawio')}>
                        {t('azureDiagram.downloadDrawio', 'Éditable dans draw.io (.drawio)')}
                      </MenuItem>
                      <MenuItem onClick={() => void handleExport('vsdx')}>
                        {t('azureDiagram.downloadVsdx', 'Visio (.vsdx)')}
                      </MenuItem>
                    </MenuList>
                  </MenuPopover>
                </Menu>
              </>
            )}
            {history.length > 1 && (
              <Dropdown
                size="small"
                aria-label={t('azureDiagram.historyLabel', 'Relevé affiché')}
                value={selectedScanId ? (historyLabel(history.find((r) => r.id === selectedScanId) ?? history[0])) : ''}
                selectedOptions={selectedScanId ? [selectedScanId] : []}
                onOptionSelect={(_, d) => {
                  if (!d.optionValue || d.optionValue === selectedScanId) return;
                  setSelectedScanId(d.optionValue);
                  setError(null);
                  void loadTopology(d.optionValue);
                }}
                style={{ minWidth: 280 }}
              >
                {history.map((r, i) => (
                  <Option key={r.id} value={r.id} text={historyLabel(r)}>
                    {i === 0 ? `${historyLabel(r)} · ${t('azureDiagram.mostRecent', 'le plus récent')}` : historyLabel(r)}
                  </Option>
                ))}
              </Dropdown>
            )}
            <Button
              appearance="primary"
              icon={doc ? <ArrowSync20Regular /> : <Play24Regular />}
              onClick={() => void handleGenerate()}
              disabled={generating || !selectedConnId}
            >
              {doc
                ? t('azureDiagram.regenerate', 'Regénérer le schéma')
                : t('azureDiagram.generate', 'Faire le schéma d’Azure')}
            </Button>
            {connections.length > 1 && (
              <Dropdown
                size="small"
                placeholder={t('nisAzure.connectionPicker', 'Connexion Azure')}
                value={selectedConn?.name ?? ''}
                selectedOptions={selectedConnId ? [selectedConnId] : []}
                onOptionSelect={(_, d) => setSelectedConnId(d.optionValue ?? null)}
                style={{ minWidth: 240 }}
              >
                {connections.map((c) => <Option key={c.id} value={c.id}>{c.name}</Option>)}
              </Dropdown>
            )}
          </>
        }
      />

      {error && (
        <MessageBar intent="error" style={{ marginBottom: 16 }}>
          <MessageBarBody>{error}</MessageBarBody>
        </MessageBar>
      )}

      {generating && (
        <Card style={{ marginBottom: 16, padding: 16 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 10 }}>
            <Spinner size="tiny" />
            <Body1>{progress ?? t('azureDiagram.working', 'Scan en cours…')}</Body1>
          </div>
          <ProgressBar />
          <Caption1 style={{ marginTop: 8, color: tokens.colorNeutralForeground3 }}>
            {t('azureDiagram.workingHint', 'Le relevé lit chaque ressource de l’abonnement : comptez quelques minutes sur un abonnement fourni.')}
          </Caption1>
        </Card>
      )}

      {/* Avertissements de collecte */}
      {meta?.truncated && (
        <MessageBar intent="warning" style={{ marginBottom: 16 }}>
          <MessageBarBody>
            <MessageBarTitle>{t('azureDiagram.truncatedTitle', 'Relevé partiel')}</MessageBarTitle>
            {t('azureDiagram.truncatedBody', 'L’abonnement dépasse le volume relevé en une passe : certaines ressources apparaissent sans leurs liens.')}
          </MessageBarBody>
        </MessageBar>
      )}
      {meta?.propertiesStripped && (
        <MessageBar intent="info" style={{ marginBottom: 16 }}>
          <MessageBarBody>
            {t('azureDiagram.strippedBody', 'Schéma allégé : le détail ARM brut de chaque ressource n’est pas conservé, la structure et les liens le sont.')}
          </MessageBarBody>
        </MessageBar>
      )}

      {loading && !generating && (
        <Card style={{ padding: 40, display: 'flex', justifyContent: 'center' }}>
          <Spinner label={t('common.loading', 'Chargement…')} />
        </Card>
      )}

      {/* Aucune connexion Azure configuree */}
      {!loading && connections.length === 0 && (
        <Card style={{ padding: 32 }}>
          <Title3>{t('azureDiagram.noConnectionTitle', 'Aucune connexion Azure')}</Title3>
          <Body1 style={{ marginTop: 8, color: tokens.colorNeutralForeground2 }}>
            {t('azureDiagram.noConnectionBody', 'Ajoutez une connexion Azure dans NIS Azure pour relever l’architecture de l’abonnement.')}
          </Body1>
        </Card>
      )}

      {/* Connexion prete, pas encore de schema */}
      {!loading && !generating && connections.length > 0 && !doc && (
        <Card style={{ padding: 40, textAlign: 'center' }}>
          <Title3>{t('azureDiagram.emptyTitle', 'Pas encore de schéma')}</Title3>
          <Body1 style={{ marginTop: 8, marginBottom: 20, color: tokens.colorNeutralForeground2 }}>
            {t('azureDiagram.emptyBody', 'Lancez un relevé : NIS365 parcourt l’abonnement et dessine les groupes de ressources, les réseaux virtuels, les sous-réseaux et les liens entre ressources.')}
          </Body1>
          <Button appearance="primary" size="large" icon={<Play24Regular />} onClick={() => void handleGenerate()}>
            {t('azureDiagram.generate', 'Faire le schéma d’Azure')}
          </Button>
        </Card>
      )}

      {/* Schema */}
      {doc && (
        <Card style={{ padding: 0, overflow: 'hidden' }}>
          <div style={{
            display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap',
            padding: '8px 14px', borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
          }}>
            <Caption1 style={{ color: tokens.colorNeutralForeground3 }}>
              {t('azureDiagram.generatedAt', 'Relevé du')}{' '}
              {new Date(doc.generatedAt).toLocaleString()}
            </Caption1>
            {history.length > 0 && history[0].id !== doc.id && (
              <Badge appearance="tint" color="warning">
                {t('azureDiagram.olderSnapshot', 'Relevé antérieur')}
              </Badge>
            )}
            <Caption1 style={{ color: tokens.colorNeutralForeground3 }}>
              {doc.dataset.resources?.length ?? 0} {t('azureDiagram.resources', 'ressources')}
              {' · '}{doc.dataset.resourceGroups?.length ?? 0} {t('azureDiagram.rgs', 'groupes')}
              {' · '}{doc.dataset.edges?.length ?? 0} {t('azureDiagram.links', 'liens')}
            </Caption1>
          </div>
          {html ? (
            <iframe
              title={t('azureDiagram.title', 'Schéma Azure')}
              srcDoc={html}
              sandbox="allow-scripts allow-downloads allow-popups allow-popups-to-escape-sandbox"
              style={{ width: '100%', height: 'calc(100vh - 260px)', minHeight: 520, border: 0, display: 'block' }}
            />
          ) : (
            <div style={{ padding: 40, display: 'flex', justifyContent: 'center' }}>
              <Spinner label={t('azureDiagram.rendering', 'Construction du schéma…')} />
            </div>
          )}
        </Card>
      )}
    </>
  );
}
